'use client';

import Image from 'next/image';
import { PartnerLogoGridBlock as IPartnerLogoGridBlock } from '../types';
import { Partners } from '../types';
import { useEffect, useState } from 'react';
import { SupportedLanguages } from '@/types/globals';

const messages = {
  fr: {
    loading: 'Compter tous nos partenaires satisfaits...',
    noPartners: 'Aucun partenaire trouvé.',
  },
  en: {
    loading: 'Counting all of our happy partners...',
    noPartners: 'No partners found.',
  },
};

export default function PartnerLogoGridBlock({
  locale,
}: {
  block: IPartnerLogoGridBlock;
  locale: SupportedLanguages;
}) {
  const [allPartners, setAllPartners] = useState<Partners[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchAllPartners() {
      setIsLoading(true);
      try {
        // Get first page and total pages count
        const firstPageResponse = await fetch(
          `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/partners?per_page=100&page=1&orderby=title&order=asc`,
        );

        if (!firstPageResponse.ok) {
          throw new Error('Failed to fetch partners');
        }

        const totalPages = parseInt(
          firstPageResponse.headers.get('X-WP-TotalPages') || '1',
        );

        // Get first page data
        const firstPageData = await firstPageResponse.json();

        // If we have more pages, fetch them in parallel
        let allData = firstPageData;
        if (totalPages > 1) {
          const remainingPagePromises = Array.from(
            { length: totalPages - 1 },
            (_, i) =>
              fetch(
                `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/partners?per_page=100&page=${i + 2}&orderby=title&order=asc`,
              ).then(res => res.json()),
          );

          const remainingPagesData = await Promise.all(remainingPagePromises);
          allData = [...firstPageData, ...remainingPagesData.flat()];
        }

        // Filter out partners with no title AND no logo
        const validPartners = allData.filter(
          (partner: Partners) =>
            partner.title?.rendered?.trim() || partner.acf?.logo?.url,
        );

        setAllPartners(validPartners);
      } catch (error) {
        console.error('Error fetching partners:', error);
        setAllPartners([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAllPartners();
  }, []); // Only fetch once on mount

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-qf-purple">{messages[locale].loading}</div>
      </div>
    );
  }

  if (allPartners.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-qf-purple">{messages[locale].noPartners}</div>
      </div>
    );
  }

  return (
    <div className={`mx-auto max-w-7xl p-4 sm:p-6 lg:p-8`}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {allPartners.map(partner => (
          <div
            key={partner.title?.rendered}
            className="rounded-lg bg-white p-3 shadow-sm"
          >
            <div
              className="flex items-center justify-center rounded-lg"
              style={{ width: '100%', height: 80 }}
            >
              {partner.acf?.logo?.url ? (
                <Image
                  src={partner.acf.logo.url}
                  alt={
                    partner.acf.logo.alt ||
                    partner.title?.rendered ||
                    'Company logo'
                  }
                  className="h-full w-full rounded-lg object-contain"
                  width={80}
                  height={80}
                />
              ) : (
                <div className="text-qf-purple text-center">
                  <p className="text-xs">
                    No logo for {partner.title?.rendered}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
